import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import {
  // MessageSquare,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  LogOut,
  X,
  User as UserIcon,
  // Users,
  Package,
  Building2,
  // Tag,
  // Receipt,
  // DollarSign,
  Store,
  // TrendingUp,
  EuroIcon,
  ShoppingCart,
  FileText,
  Shield,
  Sliders,
  Bell
} from 'lucide-react';
import { ThemeSwitcher } from '@/components/ui/ThemeSwitcher';
import { useAuthStore } from '@/stores/authStore';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useEmployeePermissions } from '@/hooks/useEmployeePermissions';
import { useChatStore } from '@/stores/chatStore';
// Chat réactivé
import chatService from '@/services/chat';
import { useWebSocket } from '@/services/websocket';
import { apiService } from '@/services/api';

const baseNavigation = [
  // { name: 'Messagerie', href: '/chat', icon: MessageSquare, module: 'chat' },
  // { name: 'Factures', href: '/invoices', icon: Receipt, module: 'invoices' },
  // { name: 'Commissions', href: '/commissions', icon: TrendingUp, module: 'commissions' },
  { name: 'Documentation', href: '/documentation', icon: FileText, module: null },
];

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const { user: currentUser, logout } = useAuthStore();
  const { hasModuleAccess, loading: permissionsLoading, permissions } = useEmployeePermissions();
  const { unreadCount, setUnreadCount, incrementUnread } = useChatStore();
  const [, setBrandLogoUrl] = useState<string | null>(null);
  const [, setBrandName] = useState<string | null>(null);

  useWebSocket((message) => {
    if (message.type === 'notification' && message.data?.kind === 'chat_new_message') {
      incrementUnread();
    }
  });

  useEffect(() => {
    let cancelled = false;
    // Load branding (logo/company name) once
    apiService
      .getGroupSettings()
      .then((settings) => {
        if (!cancelled) {
          const logo = settings?.logo_url ? String(settings.logo_url) : null;
          setBrandLogoUrl(logo);
          setBrandName(settings?.company_name ? String(settings.company_name) : null);
        }
      })
      .catch((err) => {
        console.warn('[SIDEBAR] Failed to fetch group settings (logo)', err?.response?.status || err?.message || err);
      });
    // Chat réactivé
    chatService
      .getUnreadCount()
      .then((response) => {
        if (!cancelled) {
          setUnreadCount(response.unread_count ?? 0);
        }
      })
      .catch((error) => {
        console.error('[SIDEBAR] Failed to fetch chat unread count:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [setUnreadCount]);

  // Debug: log permissions state
  console.log('[SIDEBAR] Permissions state:', {
    loading: permissionsLoading,
    permissionsCount: permissions.length,
    hasChatPermission: permissions.find(p => p.module_name === 'chat'),
    userRole: currentUser?.role
  });
  const [compact, setCompact] = useState<boolean>(() => {
    try { return localStorage.getItem('sidebar_compact') === '1'; } catch { return false; }
  });
  // Auto-compact on small screens
  const [autoCompact, setAutoCompact] = useState<boolean>(false);
  useEffect(() => {
    const onResize = () => {
      // breakpoint ~ lg (<= 1024) forces compact
      setAutoCompact(window.innerWidth <= 1024);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const effectiveCompact = compact || autoCompact;
  useEffect(() => {
    try { localStorage.setItem('sidebar_compact', compact ? '1' : '0'); } catch { }
  }, [compact]);

  // Filtrer la navigation de base selon les permissions
  const filteredBaseNav = baseNavigation.filter(item => {
    // Si pas de module défini (null), toujours afficher
    if (item.module === null) return true;

    // Si admin ou super_admin ou apporteur, afficher tout (le filtrage réel se fait via RBAC ou backend)
    if (['super_admin', 'admin', 'apporteur'].includes(currentUser?.role || '')) return true;

    // Pour les employés, vérifier les permissions
    const hasAccess = hasModuleAccess(item.module);

    return hasAccess;
  });

  // Module Achat navigation (visible only for admin / super_admin / apporteur)
  // Order: Tableau de bord => Mon compte => Clients => Points de vente => Bordereau => Commandes => Apporteur d'affaire => Administration
  const achatNav: Array<{ name: string; href: string; icon: any; separator?: boolean }> = [];
  if (['super_admin', 'admin', 'apporteur'].includes(currentUser?.role || '')) {
    achatNav.push(
      { name: 'Tableau de Bord', href: '/achat/dashboard', icon: BarChart3 },
      { name: 'Mon compte', href: '/settings', icon: UserIcon },
      // { name: 'Prospects', href: '/achat/prospects', icon: Users },
      { name: 'Clients', href: '/achat/clients', icon: Building2 },
      { name: 'Points de vente', href: '/achat/sales-points', icon: Store },
      { name: 'Produits', href: '/achat/consumables', icon: Package },
      { name: 'Bordereau de Prix', href: '/achat/pricing', icon: EuroIcon },
      { name: 'Commandes client', href: '/achat/orders', icon: ShoppingCart },
    );

    if (currentUser?.role === 'super_admin') {
      achatNav.push({ name: 'Configurateur', href: '/achat/product-config', icon: Sliders });
    }

    // Seuls les super_admins et les nouveaux admins globaux voient la liste des apporteurs
    if (['super_admin', 'admin'].includes(currentUser?.role || '')) {
      // Masqué au profit de l'onglet dans Administration
      // achatNav.push({ name: "Apporteurs d'Affaires", href: '/super-admin/admins', icon: Target });
    }
  }

  // Combiner toutes les routes dans une seule liste
  const allNavigation: Array<{ name: string; href: string; icon: any; separator?: boolean }> = [...achatNav, ...filteredBaseNav];

  // Insérer "Mes Rappels" juste après Documentation (avec séparateur)
  if (['super_admin', 'admin', 'apporteur'].includes(currentUser?.role || '')) {
    const docIndex = allNavigation.findIndex(item => item.href === '/documentation');
    if (docIndex !== -1) {
      allNavigation.splice(docIndex + 1, 0, {
        name: 'Mes Rappels',
        href: '/achat/reminders',
        icon: Bell,
        separator: true,
      });
    }
  }

  // Commission button for super_admin and apporteur
  // if (['super_admin', 'apporteur'].includes(currentUser?.role || '')) {
  //   allNavigation.push({ name: 'Commissions', href: '/commissions', icon: TrendingUp });
  // }

  // Administration button for super_admin and admin only
  if (['super_admin', 'admin'].includes(currentUser?.role || '')) {
    allNavigation.push({ name: 'Administration', href: '/administration', icon: Shield });
  }

  const sidebarContent = (
    <div
      className={cn(
        'flex flex-col h-screen bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transition-all duration-300',
        mobileOpen ? 'w-64' : (effectiveCompact ? 'w-16' : 'w-64')
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-12 border-b-[3px] border-green-700">
        <div className="flex items-center gap-2 h-full">
          <img
            src="/NOVA - Goutte ALLIANCE.png"
            alt="Nova logo"
            className="w-10 h-10 object-contain rounded-md bg-white"
          />
          {(!effectiveCompact || mobileOpen) && (
            <span className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              NOVA
            </span>
          )}
        </div>
        <button
          onClick={mobileOpen ? onMobileClose : () => setCompact(!compact)}
          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
        >
          {mobileOpen ? (
            <X className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          ) : effectiveCompact ? (
            <ChevronRight className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          ) : (
            <ChevronLeft className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          )}
        </button>
      </div>


      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
        {allNavigation.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          const Icon = item.icon;
          const isChat = item.href === '/chat';
          const showSeparator = item.separator;

          const linkContent = (
            <Link
              key={item.name}
              to={item.href}
              onClick={mobileOpen ? onMobileClose : undefined}
              className={cn(
                'flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200',
                isActive
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800',
                (effectiveCompact && !mobileOpen) && 'justify-center'
              )}
            >
              <Icon className={cn('h-5 w-5 flex-shrink-0', isActive && 'text-white')} />
              {(!effectiveCompact || mobileOpen) && (
                <div className="flex-1 flex items-center justify-between">
                  <span className="font-medium">{item.name}</span>
                  {isChat && unreadCount > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-600 text-white min-w-[1.5rem] text-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </div>
              )}
            </Link>
          );

          if (effectiveCompact && !mobileOpen) {
            return (
              <div key={item.name}>
                {showSeparator && (
                  <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
                )}
                <Tooltip>
                  <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                  <TooltipContent side="right">
                    <div className="flex items-center gap-2">
                      <span>{item.name}</span>
                      {isChat && unreadCount > 0 && (
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-600 text-white">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
            );
          }

          return (
            <div key={item.name}>
              {showSeparator && (
                <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
              )}
              {linkContent}
            </div>
          );
        })}
      </nav>

      {/* Footer - iOS safe area padding */}
      <div className="border-t border-slate-200 dark:border-slate-800 p-2 pb-safe space-y-2" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
        {/* Theme Switcher */}
        <div className={cn('flex', (effectiveCompact && !mobileOpen) ? 'justify-center' : 'px-2')}>
          <ThemeSwitcher showLabel={!effectiveCompact || mobileOpen} />
        </div>

        {/* Direct Logout - Enhanced touch target for mobile */}
        <button
          onClick={() => logout()}
          className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50 active:bg-red-100 dark:bg-slate-900 dark:border-red-400/40 dark:hover:bg-red-950/20 text-sm font-medium transition-colors min-h-[44px]"
        >
          <LogOut className="h-4 w-4" />
          {(!effectiveCompact || mobileOpen) && <span>Déconnexion</span>}
        </button>
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={0}>
      {/* Desktop sidebar: always visible, hidden on mobile */}
      <div className="hidden md:block">
        {sidebarContent}
      </div>

      {/* Mobile drawer: overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onMobileClose}
          />
          {/* Drawer */}
          <div className="relative z-10 h-full w-fit">
            {sidebarContent}
          </div>
        </div>
      )}
    </TooltipProvider>
  );
}
